/**
 * Aurora Combobox — async-loaded multi-select met chips.
 *
 * UX-patroon:
 *  - De trigger-balk toont ALLEEN de geselecteerde chips + een caret.
 *    Geen inline tekst-input — klik (of Enter/Space/ArrowDown met
 *    keyboard-focus) opent de dropdown.
 *  - Het zoekveld zit BOVENIN de dropdown-popover en krijgt focus bij
 *    openen. Type → debounced async-search → gesectioneerde lijst.
 *  - Klik op een optie togglet de selectie (checkmark). Bij een
 *    meervoudige keuze (`maxItems` > 1) blijft de lijst open, zodat je
 *    in één keer meerdere opties kiest; een klik buiten de lijst, Tab
 *    of Escape sluit hem. Bij één keuze (`maxItems` 1) sluit hij na de
 *    keuze — zo werken de "voeg toe"-zoekers.
 *  - Wat gekozen is blijft zichtbaar: als chip, én aangevinkt in de
 *    lijst — ook als de zoekopdracht van de server het niet (meer)
 *    teruggeeft. Daarvoor onthoudt het component elke optie die het
 *    ooit kreeg.
 *  - Een boom (opties met `parentValue`, bv. hoofd- en subcategorieën):
 *    per ouder een groep, de ouder bovenaan en zelf te kiezen, de
 *    kinderen ingesprongen eronder met hun eigen naam (`listLabel`). De
 *    chip toont het volle `label`. Vindt de zoeker alleen een kind, dan
 *    staat zijn ouder er als kop van de groep bij.
 *  - Backspace in een leeg zoekveld → laatste chip verwijderen.
 *  - ArrowDown/ArrowUp verplaatsen, Enter togglet (en Spatie, zolang
 *    het zoekveld leeg is — daarna is een spatie gewoon tekst);
 *    Escape sluit en geeft focus terug aan de trigger.
 *
 * Bewust gebouwd zonder external dep — de paar libraries die dit
 * netjes doen (react-select, downshift, cmdk) zijn ofwel zwaar of
 * passen niet bij Aurora's CSS-Modules-stack. Voor onze huidige
 * scope (admin-filter inputs) is een lichte eigen implementatie
 * de juiste keuze.
 */
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown, Check, X } from '@phosphor-icons/react';
import { formatLabel, useAuroraLabels } from '../auroraLabels';
import { useAnchoredDropdown } from '../useAnchoredDropdown';
import styles from './Combobox.module.css';

export interface ComboboxOption {
  /** Stabiele id (UUID) waarmee de Combobox selecties identificeert. */
  value: string;
  /** Wat in de chip + dropdown wordt getoond. */
  label: string;
  /** Optioneel secundair label, bv. type-prefix of email. */
  hint?: string;
  /**
   * Optionele sectie-header. Opties met dezelfde `section` worden
   * gegroepeerd onder één overline-kop (insertion-order behouden).
   * Zonder section verschijnen opties zonder kop.
   */
  section?: string;
  /** Optionele rechtsgelijnde telling (bv. aantal leden / entries). */
  count?: number;
  /**
   * LOKALE (issue 1176): de waarde van de ouder-optie, voor een boom zoals hoofd- en subcategorieën. De lijst zet elke
   * ouder bovenaan zijn groep (zelf ook te kiezen) met zijn kinderen ingesprongen eronder. Geeft de zoeker alleen een
   * kind terug, dan haalt de lijst de ouder erbij als kop van de groep — uit wat een eerdere fetch opleverde.
   */
  parentValue?: string;
  /**
   * LOKALE (issue 1176): wat er in de lijst staat als dat korter is dan `label`, bv. alleen de eigen naam van een
   * subcategorie ("Onboarding") onder zijn hoofdcategorie. `label` blijft voor de chip ("Compliance › Onboarding") en
   * voor de naam die een schermlezer hoort: de inspringing ziet hij niet.
   */
  listLabel?: string;
}

interface TreeItem {
  opt: ComboboxOption;
  depth: number;
  /** De sectie van de bovenste ouder: een groep blijft bij elkaar, ook als een kind een andere sectie zou hebben. */
  section: string;
  hasChildren: boolean;
}

/**
 * Zet opties met een `parentValue` in boomvolgorde: elke ouder met daaronder zijn kinderen. Een ouder die zelf niet in
 * de lijst staat maar wel bekend is (`lookup`), komt erbij — op de plek van zijn eerste kind. Zonder `parentValue`
 * blijft de volgorde zoals hij binnenkomt.
 */
function inTreeOrder(options: ComboboxOption[], lookup: (value: string) => ComboboxOption | undefined): TreeItem[] {
  if (!options.some((option) => option.parentValue)) {
    return options.map((opt) => ({ opt, depth: 0, section: opt.section ?? '', hasChildren: false }));
  }

  const byValue = new Map(options.map((option) => [option.value, option]));
  const all = [...options];
  for (let i = 0; i < all.length; i++) {
    const parent = all[i].parentValue;
    if (parent === undefined || byValue.has(parent)) continue;
    const known = lookup(parent);
    if (known) {
      byValue.set(parent, known);
      all.push(known);
    }
  }

  const parentOf = (option: ComboboxOption) =>
    option.parentValue !== undefined && option.parentValue !== option.value && byValue.has(option.parentValue)
      ? option.parentValue
      : undefined;
  const children = new Map<string, ComboboxOption[]>();
  for (const option of all) {
    const parent = parentOf(option);
    if (parent !== undefined) children.set(parent, [...(children.get(parent) ?? []), option]);
  }

  const out: TreeItem[] = [];
  const placed = new Set<string>();
  const place = (option: ComboboxOption, depth: number, section: string) => {
    if (placed.has(option.value)) return;
    placed.add(option.value);
    const kids = children.get(option.value) ?? [];
    out.push({ opt: option, depth, section, hasChildren: kids.length > 0 });
    for (const kid of kids) place(kid, depth + 1, section);
  };

  for (const option of options) {
    // Omhoog naar de bovenste ouder; `seen` breekt een kring in de data af.
    let root = option;
    const seen = new Set([root.value]);
    for (let up = parentOf(root); up !== undefined && !seen.has(up); up = parentOf(root)) {
      seen.add(up);
      root = byValue.get(up)!;
    }
    place(root, 0, root.section ?? '');
  }

  return out;
}

export interface ComboboxProps {
  /** Huidige selectie — array van value-strings. Controlled. */
  value: string[];
  /** Resolveert value → label voor chip-rendering. */
  resolveLabel: (value: string) => string | undefined;
  /**
   * Async fetch van suggesties. Wordt aangeroepen wanneer de input
   * verandert (na debounce) en éénmaal op focus met lege query.
   * Resolve met de complete lijst — Combobox filtert niet zelf.
   */
  fetchOptions: (query: string) => Promise<ComboboxOption[]>;
  onChange: (next: string[]) => void;
  placeholder?: string;
  /**
   * Max aantal chips. Verdere selecties worden geblokkeerd. Bij 1 is het een
   * enkele keuze: de lijst sluit dan na het kiezen; daarboven blijft hij open.
   */
  maxItems?: number;
  /** Debounce-ms voor de fetch. 200ms is een fijne default. */
  debounceMs?: number;
  /** ARIA-label voor de input (geen visible label). */
  ariaLabel?: string;
  /**
   * De twee statusteksten in de lijst — issue 419.
   *
   * ⚠️ DE DEFAULTS ZIJN ENGELS, EN DAT IS EEN KEUZE MET EEN RAND. Een
   * Aurora-component hoort geen `useTranslation()` te doen: dan hangt de
   * design-laag aan de vertaal-bundel van de app, en dat is bij `DataTable` in
   * dezelfde map bewust andersom besloten. De prijs is dat een aanroeper die ze
   * vergeet, stil op Engels terugvalt.
   *
   * Die prijs wordt betaald door `auroraNoLiteralText.test.ts`: die keurt
   * letterlijke zinnen in de JSX van Aurora-componenten af. Vergeten is dus geen
   * optie meer — niet omdat het component het onmogelijk maakt, maar omdat een
   * poort het ziet.
   */
  emptyText?: string;
  loadingText?: string;
}

export function Combobox({
  value,
  resolveLabel,
  fetchOptions,
  onChange,
  placeholder,
  maxItems = 10,
  debounceMs = 200,
  ariaLabel,
  emptyText = 'No results.',
  loadingText = 'Loading…',
}: ComboboxProps) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = useId();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const multi = maxItems > 1;
  // Elke optie die een fetch ooit opleverde, op waarde. Een async zoeker geeft bij een volgende zoekterm (of bij
  // heropenen met een lege term) andere treffers terug; zonder dit geheugen viel een gekozen optie dan uit de lijst
  // en leek de keuze weg.
  const seenRef = useRef(new Map<string, ComboboxOption>());
  // LOKALE afwijking (issue 294): het kruisje op een chip heette vast
  // "Remove …". De tekst is overal dezelfde, dus hij komt uit de AuroraLabels-
  // context en niet als prop zoals `emptyText` — die verschilt wél per aanroep.
  const labels = useAuroraLabels();

  // Focus het zoekveld in de dropdown zodra die opent — de gebruiker
  // kan meteen typen zonder eerst te klikken. Bij sluiten geven we
  // focus terug aan de trigger (toetsenbord-flow blijft op het veld).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      // Wacht op de portal-mount + meting voordat we focussen.
      const handle = window.setTimeout(() => inputRef.current?.focus(), 0);
      wasOpenRef.current = true;
      return () => window.clearTimeout(handle);
    }
    if (!open && wasOpenRef.current) {
      wasOpenRef.current = false;
      setQuery('');
    }
  }, [open]);

  /*
   * Dropdown wordt via React Portal naar document.body gerendered
   * zodat 'ie boven elke parent-overflow (bv. DataTable's
   * `overflow: hidden`-wrapper) heen tekent. We meten de positie
   * van de control-row en mirroren die naar fixed coords zodat de
   * dropdown precies onder de input verschijnt. Op resize / scroll
   * re-measuren we — anders zou de dropdown losraken van z'n
   * trigger; bij scroll zonder React-render (zie `useAnchoredDropdown`),
   * anders loopt de lijst zichtbaar achter op de pagina.
   */
  const dropdownPos = useAnchoredDropdown(open, wrapRef, dropdownRef);

  // Async-fetch met debounce + race-bescherming. De laatste fetch wint
  // ook als een eerdere langer duurt — anders zou een trage server-
  // reactie de UI laten flikkeren met stale suggesties.
  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(async () => {
      setLoading(true);
      const myQuery = query;
      try {
        const result = await fetchOptions(myQuery);
        for (const option of result) seenRef.current.set(option.value, option);
        // Race-check: als de query inmiddels is veranderd, laat het.
        setOptions((prev) => (myQuery === queryRef.current ? result : prev));
      } finally {
        setLoading(false);
      }
    }, debounceMs);
    return () => window.clearTimeout(handle);
  }, [query, open, debounceMs, fetchOptions]);

  // Mirror query in een ref voor de race-check (closure boven kan
  // anders alleen de waarde lezen van toen het effect ge-scheduled
  // werd, niet de meest actuele).
  const queryRef = useRef(query);
  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  // Click-outside sluit. We checken zowel het wrap-element (chips +
  // input) als de portal-dropdown — anders sluit een klik op een
  // optie de dropdown vóór de selectie kan landen.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      const inWrap = wrapRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inWrap && !inDropdown) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  /** De naam van een waarde: wat de aanroeper zegt, anders wat een fetch ooit opleverde, anders de waarde zelf. */
  function labelOf(v: string): string {
    return resolveLabel(v) ?? seenRef.current.get(v)?.label ?? v;
  }

  // Alle opties blijven zichtbaar — ook al-geselecteerde. Die tonen
  // een checkmark en zijn toggle-baar (klik = deselect). Dat matcht
  // het multi-select-pattern uit de referentie-mockup (suggesties +
  // alle, met vinkjes op de gekozen items).
  //
  // LOKALE (issue 1176): zonder zoekterm staat ook elke gekozen waarde in de lijst die de fetch niet teruggaf —
  // bovenaan, zonder sectie-kop. Een async zoeker geeft bij een lege term een eerste pagina, en daar hoeft wat je
  // net via een zoekterm koos niet in te zitten; dan stond het niet aangevinkt in de lijst en leek de keuze weg.
  const flatOptions = useMemo(() => {
    if (query.trim() !== '') return options;
    const listed = new Set(options.map((option) => option.value));
    const missing = value
      .filter((v) => !listed.has(v))
      .map((v): ComboboxOption => {
        const known = seenRef.current.get(v);
        return {
          value: v,
          label: resolveLabel(v) ?? known?.label ?? v,
          hint: known?.hint,
          count: known?.count,
          // In een boom blijft een gekozen kind onder zijn ouder staan, met zijn eigen naam.
          parentValue: known?.parentValue,
          listLabel: known?.listLabel,
        };
      });
    return missing.length === 0 ? options : [...missing, ...options];
  }, [options, value, query, resolveLabel]);

  // Groepeer per section met insertion-order behoud. Opties zonder
  // section vallen onder een lege-string-key (geen header gerenderd).
  // Binnen een sectie in boomvolgorde als de opties een `parentValue` hebben (LOKALE, issue 1176).
  const grouped = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, TreeItem[]>();
    for (const item of inTreeOrder(flatOptions, (v) => seenRef.current.get(v))) {
      if (!map.has(item.section)) {
        map.set(item.section, []);
        order.push(item.section);
      }
      map.get(item.section)!.push(item);
    }
    // Bouw flat-index-map zodat keyboard-highlight over secties heen
    // werkt (ArrowDown loopt door de volgorde van rendering).
    let flatIdx = 0;
    return order.map((section) => {
      const items = map.get(section)!.map((item) => ({ ...item, flatIdx: flatIdx++ }));
      return { section, items };
    });
  }, [flatOptions]);

  // De opties in de volgorde waarin ze op het scherm staan: het groeperen kan de volgorde van de fetch veranderen,
  // en de pijltjes horen te lopen zoals je kijkt.
  const ordered = useMemo(() => grouped.flatMap(({ items }) => items.map(({ opt }) => opt)), [grouped]);
  const activeIndex = ordered.length === 0 ? -1 : Math.min(Math.max(highlighted, 0), ordered.length - 1);
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  /**
   * Toggle een waarde. LOKALE (issue 1176): bij een meervoudige keuze blijft de lijst open en houdt het zoekveld de
   * focus, zodat je de volgende kiest zonder opnieuw te openen. Vroeger kon dat alleen met Ctrl/Cmd ingedrukt, en dat
   * vond niemand. Bij één keuze sluit de lijst en gaat de focus terug naar de trigger.
   */
  function toggleValue(v: string) {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      if (value.length >= maxItems) return;
      onChange([...value, v]);
    }
    setQuery('');
    if (multi) {
      inputRef.current?.focus();
    } else {
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  function removeValue(v: string) {
    onChange(value.filter((x) => x !== v));
  }

  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && query === '' && value.length > 0) {
      e.preventDefault();
      removeValue(value[value.length - 1]);
      return;
    }
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (ordered.length > 0) setHighlighted(Math.min(ordered.length - 1, activeIndex + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(Math.max(0, activeIndex - 1));
    } else if (e.key === 'Enter' || (e.key === ' ' && query === '')) {
      // Spatie togglet alleen in een leeg zoekveld: in een zoekterm ("Jan de Vries") is het gewoon een spatie.
      e.preventDefault();
      const opt = ordered[activeIndex];
      if (opt) toggleValue(opt.value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      // De lijst staat in een portal aan het eind van de pagina; zonder dit liep Tab daarheen in plaats van naar het
      // volgende veld. Geen preventDefault: de focus staat dan op de trigger, en de browser gaat van daar verder.
      close();
    }
  }

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <div
        className={styles.controlRow}
        role="combobox"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        ref={triggerRef}
        onClick={(e) => {
          // LOKALE (issue 1176): staat de Combobox binnen een <label>, dan stuurt een klik op het veld de klik door
          // naar het eerste bedienbare element erin — het kruisje van de eerste chip. Heropenen haalde zo de vorige
          // keuze weg. preventDefault zet die doorsturing van de label uit; het openen zelf blijft gewoon werken.
          e.preventDefault();
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          } else if (e.key === 'Backspace' && value.length > 0) {
            e.preventDefault();
            removeValue(value[value.length - 1]);
          }
        }}
      >
        {value.length === 0 ? (
          <span className={styles.placeholder}>{placeholder}</span>
        ) : (
          value.map((v) => (
            <span key={v} className={styles.chip}>
              <span className={styles.chipLabel}>{labelOf(v)}</span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={(e) => {
                  e.stopPropagation();
                  removeValue(v);
                }}
                aria-label={formatLabel(labels.comboboxRemove, { label: labelOf(v) })}
              >
                <X size={12} weight="bold" />
              </button>
            </span>
          ))
        )}
        <span className={styles.caret} aria-hidden="true">
          <CaretDown size={12} weight="regular" />
        </span>
      </div>
      {open && dropdownPos &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              bottom: dropdownPos.bottom,
              left: dropdownPos.left,
              width: dropdownPos.width,
              maxHeight: dropdownPos.maxHeight,
            }}
          >
            <div className={styles.searchRow}>
              <input
                ref={inputRef}
                className={styles.searchInput}
                type="text"
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
                aria-label={ariaLabel}
                placeholder={placeholder}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlighted(0);
                }}
                onKeyDown={onKey}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                name={`aurora-combobox-${listboxId}`}
                data-form-type="other"
              />
            </div>
            <ul id={listboxId} role="listbox" aria-multiselectable={multi || undefined} className={styles.optionList}>
            {loading && flatOptions.length === 0 ? (
              <li className={styles.statusItem} aria-live="polite">
                {loadingText}
              </li>
            ) : flatOptions.length === 0 ? (
              <li className={styles.statusItem}>{emptyText}</li>
            ) : (
              grouped.map(({ section, items }) => (
                <li key={section || '__nosection'} className={styles.group}>
                  {section && (
                    <div className={styles.groupHeader} aria-hidden="true">
                      {section}
                    </div>
                  )}
                  <ul className={styles.groupList} role="group" aria-label={section || undefined}>
                    {items.map(({ opt, flatIdx, depth, hasChildren }) => {
                      const selected = value.includes(opt.value);
                      // Een kind toont alleen zijn eigen naam; een schermlezer ziet de inspringing niet en hoort het
                      // hele pad (de zichtbare naam zit daarin, WCAG 2.5.3).
                      const shortened = opt.listLabel !== undefined && opt.listLabel !== opt.label;
                      return (
                        <li
                          key={opt.value}
                          id={optionId(flatIdx)}
                          role="option"
                          aria-selected={selected}
                          aria-label={shortened ? [opt.label, opt.hint].filter(Boolean).join(', ') : undefined}
                          className={[
                            styles.option,
                            depth > 0 ? styles.optionChild : '',
                            hasChildren ? styles.optionParent : '',
                            flatIdx === activeIndex ? styles.optionHighlighted : '',
                          ].filter(Boolean).join(' ')}
                          onMouseDown={(e) => {
                            // mousedown i.p.v. click — click triggert na
                            // blur-on-input wat eerst de dropdown sluit.
                            e.preventDefault();
                            if (e.button !== 0) return;
                            toggleValue(opt.value);
                          }}
                          onMouseEnter={() => setHighlighted(flatIdx)}
                        >
                          <span className={styles.checkSlot} aria-hidden="true">
                            {selected && <Check size={14} weight="bold" />}
                          </span>
                          <span className={styles.optionBody}>
                            <span className={styles.optionLabel}>{shortened ? opt.listLabel : opt.label}</span>
                            {opt.hint && <span className={styles.optionHint}>{opt.hint}</span>}
                          </span>
                          {opt.count != null && (
                            <span className={styles.optionCount}>{opt.count}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))
            )}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}
